import Vue from 'vue';
import { each, extend, has, find, get, isObject, reduce } from 'lodash';

import * as types from './mutation-types';
import { rehash } from '../utils/hash';
import { setUpdateStatus } from '../utils/reactive';
import actions from '../utils/actions';
import { assert } from '../../utils/assert';

// Create a reverse look up table of all actions
const actionsLUT = reduce(actions, (lut, value, key) => {
  lut[value] = key;
  return lut;
}, {});

function update(state) {
  if (!state.refreshing) {
    setUpdateStatus(true);

    rehash(state.items);
    state.refreshing = true;
  }
}

export default {
  /**
   * Inserts the given item at the given index.
   * Provides some default values in case they haven't been set.
   *
   * @param state
   * @param index
   * @param item
   */
  [types.ADD]: (state, { index, item }) => {
    state.items.splice(index, 0, extend({
      uuid: state.counter += 1,
      loading: true,
      errors: null,
      showOptions: false,
      data: {},
    }, item));

    update(state);
  },

  /**
   * Updates the panel at the given index.
   * This only updates the values defined under the "changes" key.
   *
   * @param state
   * @param uuid
   * @param changes
   */
  [types.UPDATE]: (state, { uuid, changes, merge }) => {
    const item = find(state.items, ['uuid', uuid]);
    if (!item) return;

    each(changes, (data, property) => {
      if (merge && isObject(data)) {
        each(data, (value, key) => {
          Vue.set(item[property], key, data[key]);
        });
      } else {
        Vue.set(item, property, data);
      }
    });

    // Only rehash if the ID got changed
    if (changes.id) {
      update(state);
    }
  },

  /**
   * Updates a property inside the data for the specified panel.
   *
   * @param state
   * @param uuid
   * @param key
   * @param value
   */
  [types.FILL]: (state, {
    uuid, key, value, blockWatcher,
  }) => {
    const item = find(state.items, ['uuid', uuid]);
    if (!item) return;

    if (blockWatcher) {
      setUpdateStatus(true);
    }

    const realKey = key;
    const action = has(actions, realKey) ? realKey : actions.modelUpdated;

    // Fetch all subscribers, even the ones with a wildcard
    const subs = state.subscribers[action] || [];
    const wildcard = subs['*'] || [];
    const subscribers = wildcard.concat(subs[item.key] || []);

    switch (action) {
      case actions.modelDeleted:
        each(subscribers, fn => fn(item.data, item.id, state.items.indexOf(item)));
        break;

      case actions.modelUpdated:
      default: {
        // Vue.set does not work with dotted syntax, so fix that
        const dot = key.lastIndexOf('.');
        let { data } = item;

        if (dot > 0) {
          data = get(data, key.substring(0, dot));
          key = key.substring(dot + 1);
        }

        const oldValue = data[key];

        if (oldValue !== value) {
          Vue.set(data, key, value);
          each(subscribers, fn => fn(item.data, item.id, realKey, value, oldValue));
        }
        break;
      }
    }

    // Needs to be in the next tick
    if (blockWatcher) {
      Vue.nextTick(() => {
        setUpdateStatus(false);
      });
    }
  },

  /**
   * Closes the item (removal) at the given index.
   *
   * @param state
   * @param index
   */
  [types.CLOSE]: (state, index) => {
    state.items.splice(index, 1);
    update(state);
  },

  [types.REFRESH]: (state, callback) => {
    state.subscribers = {}; // They will be filled again in the next "tick"

    if (typeof callback === 'boolean') {
      state.refreshing = callback;
    } else {
      state.items = [];
      state.refreshing = true;

      callback();

      state.refreshing = false;
    }

    setUpdateStatus(false);
  },

  [types.INSERT]: (state, item) => {
    state.items.push(item);
  },

  [types.SUBSCRIBE]: (state, { action, type, func }) => {
    assert(actionsLUT[action] !== undefined, 'Invalid action for subscription');

    let perType = state.subscribers[action];

    if (!perType) {
      perType = {};
      state.subscribers[action] = perType;
    }

    let subs = perType[type];

    if (!subs) {
      subs = [];
      perType[type] = subs;
    }

    subs.push(func);
  },

  [types.OPTIONS]: (state, index) => {
    const panel = state.items[index];
    panel.showOptions = !panel.showOptions;
  },

};



// WEBPACK FOOTER //
// ./resources/assets/js/view-stack/store/mutations.js